import type { GridProps } from "@tql/ui-components-react/kendo/grid";

export type GridCompositeFilterDescriptor = NonNullable<GridProps["filter"]>;
type GridFilterArray = GridCompositeFilterDescriptor["filters"];
export type GridFilterDescriptor = GridFilterArray[number];
type GridLeafFilterDescriptor = GridFilterDescriptor & {
	field?: unknown;
	value?: unknown;
};

type SanitizedResult = {
	descriptor?: GridLeafFilterDescriptor;
	mutated: boolean;
};

type CollectionSanitizationResult = {
	sanitizedFilters: GridFilterDescriptor[];
	mutated: boolean;
};

const cloneFilterValue = (value: unknown): unknown => {
	if (value instanceof Date) {
		return new Date(value);
	}

	if (Array.isArray(value)) {
		return value.map((entry) => cloneFilterValue(entry));
	}

	return value;
};

const isCompositeFilterDescriptor = (
	descriptor: GridFilterDescriptor | undefined,
): descriptor is GridCompositeFilterDescriptor => {
	if (!descriptor || typeof descriptor !== "object") {
		return false;
	}

	return Array.isArray((descriptor as { filters?: unknown }).filters);
};

const isLeafFilterDescriptor = (
	descriptor: GridFilterDescriptor | undefined,
): descriptor is GridLeafFilterDescriptor => {
	if (!descriptor || typeof descriptor !== "object") {
		return false;
	}

	return "field" in descriptor && !isCompositeFilterDescriptor(descriptor);
};

const hasFilterValue = (descriptor: GridLeafFilterDescriptor): boolean => {
	const value = (descriptor as { value?: unknown }).value;
	if (value === null || value === undefined) {
		return false;
	}
	if (typeof value === "string") {
		return value.trim() !== "";
	}
	return true;
};

const sanitizeLeafFilter = (
	descriptor: GridLeafFilterDescriptor,
	allowedFields: Set<string>,
): SanitizedResult => {
	const field =
		typeof descriptor.field === "string" ? descriptor.field : undefined;
	if (!field || !allowedFields.has(field)) {
		return { mutated: true };
	}

	return { descriptor, mutated: false };
};

const sanitizeFilterCollection = (
	filters: GridFilterArray | undefined,
	allowedFields: Set<string>,
): CollectionSanitizationResult => {
	const sanitizedFilters: GridFilterDescriptor[] = [];
	let mutated = false;

	if (!Array.isArray(filters)) {
		return { sanitizedFilters, mutated: true };
	}

	const processCandidate = (
		candidate: GridFilterDescriptor | undefined,
	): boolean => {
		if (!candidate) {
			return true;
		}

		if (isCompositeFilterDescriptor(candidate)) {
			const cleanedChild = sanitizeCompositeFilterDescriptor(
				candidate,
				allowedFields,
			);
			if (!cleanedChild) {
				return true;
			}

			sanitizedFilters.push(cleanedChild);
			return cleanedChild !== candidate;
		}

		if (!isLeafFilterDescriptor(candidate)) {
			return true;
		}

		const { descriptor, mutated: leafMutated } = sanitizeLeafFilter(
			candidate,
			allowedFields,
		);
		if (descriptor) {
			sanitizedFilters.push(descriptor);
		}
		return leafMutated;
	};

	for (const candidate of filters) {
		if (processCandidate(candidate)) {
			mutated = true;
		}
	}

	return { sanitizedFilters, mutated };
};

export const sanitizeCompositeFilterDescriptor = (
	filter: GridCompositeFilterDescriptor,
	allowedFields: Set<string>,
): GridCompositeFilterDescriptor | undefined => {
	const { sanitizedFilters, mutated } = sanitizeFilterCollection(
		filter.filters,
		allowedFields,
	);

	if (sanitizedFilters.length === 0) {
		return undefined;
	}

	if (!mutated && sanitizedFilters.length === filter.filters.length) {
		return filter;
	}

	return {
		...filter,
		filters: sanitizedFilters,
	};
};

export const collectActiveFilterFields = (
	filter?: GridCompositeFilterDescriptor | null,
): Set<string> => {
	const fields = new Set<string>();
	if (!filter || !Array.isArray(filter.filters)) {
		return fields;
	}

	const visit = (descriptor: GridFilterDescriptor | undefined) => {
		if (!descriptor) {
			return;
		}

		if (isCompositeFilterDescriptor(descriptor)) {
			for (const inner of descriptor.filters ?? []) {
				visit(inner);
			}
			return;
		}

		if (isLeafFilterDescriptor(descriptor)) {
			const field =
				typeof descriptor.field === "string" ? descriptor.field : undefined;
			if (field && hasFilterValue(descriptor)) {
				fields.add(field);
			}
		}
	};

	for (const descriptor of filter.filters) {
		visit(descriptor);
	}

	return fields;
};

export const areSetsEqual = (
	left: Set<string>,
	right: Set<string>,
): boolean => {
	if (left.size !== right.size) {
		return false;
	}

	for (const value of left) {
		if (!right.has(value)) {
			return false;
		}
	}

	return true;
};

const serializeFilter = (
	filter?: GridCompositeFilterDescriptor | null,
): string => {
	return (
		JSON.stringify(filter ?? null, (_: string, value: unknown) => {
			if (value instanceof Date) {
				return value.toISOString();
			}
			return value;
		}) ?? ""
	);
};

export const areFiltersEqual = (
	left?: GridCompositeFilterDescriptor | null,
	right?: GridCompositeFilterDescriptor | null,
): boolean => {
	return serializeFilter(left) === serializeFilter(right);
};

const cloneLeafDescriptor = (
	descriptor: GridLeafFilterDescriptor,
): GridLeafFilterDescriptor => ({
	...descriptor,
	value: cloneFilterValue(descriptor.value),
});

export const cloneCompositeFilterDescriptor = (
	filter: GridCompositeFilterDescriptor,
): GridCompositeFilterDescriptor => ({
	...filter,
	filters: filter.filters.map((descriptor) =>
		isCompositeFilterDescriptor(descriptor)
			? cloneCompositeFilterDescriptor(descriptor)
			: cloneLeafDescriptor(descriptor as GridLeafFilterDescriptor),
	) as GridFilterArray,
});
